/**
 * Phase 1.7 — FilterBar.
 *
 * Tend's pattern: a single horizontal strip above the card grid containing
 * search input, dropdown filters, primary CTA(s), and a Clear-filters
 * action. We render whatever the parent passes — this is a layout
 * component, not a control library.
 *
 * Slots:
 *   - search    optional <input> (caller provides value/onChange)
 *   - filters   array of arbitrary nodes (Selects, popovers, etc)
 *   - actions   right-aligned action group
 *   - onClear   optional clear-filters callback; renders the link only if
 *               the prop is supplied AND `hasActiveFilters` is true
 *
 * The bar is sticky-eligible — wrap it in a sticky parent and it'll glue
 * to the top of the scroll container without extra work.
 */

import { Search, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface FilterBarProps {
  search?: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
  };
  filters?: ReactNode[];
  actions?: ReactNode;
  onClear?: () => void;
  hasActiveFilters?: boolean;
  className?: string;
}

export function FilterBar({
  search,
  filters,
  actions,
  onClear,
  hasActiveFilters,
  className,
}: FilterBarProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface px-3 py-3',
        'dark:bg-surface-dark dark:border-border',
        className,
      )}
    >
      {search ? (
        <div className="relative min-w-[12rem] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder ?? 'Search'}
            className={cn(
              'h-9 w-full rounded-md border border-border bg-surface pl-9 pr-3 text-sm shadow-sm',
              'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt-orange',
              'dark:bg-surface-raised-dark dark:border-border dark:focus-visible:ring-burnt-orange-dark',
            )}
          />
        </div>
      ) : null}
      {filters?.length ? (
        <div className="flex flex-wrap items-center gap-2">{filters}</div>
      ) : null}
      <div className="ml-auto flex items-center gap-2">
        {onClear && hasActiveFilters ? (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-charcoal dark:hover:text-charcoal-dark"
          >
            <X className="h-3 w-3" />
            Clear filters
          </button>
        ) : null}
        {actions}
      </div>
    </div>
  );
}
